import { matchRoutes, Navigate, renderMatches, useLocation } from 'react-router-dom'
import { orderedRoutes, routes } from './routes'
import ReactModal from 'react-modal'
import TopBar from '../Sidebar/TopBar'
import Notifications from '../Notifications'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import Sidebar from '../Sidebar/Sidebar'
import { userContext } from '../../context/UserContext'
import { ErrorBoundary } from 'react-error-boundary'
import SmallScreenNotice from '../SmallScreenNotice/SmallScreenNotice'

/**
 * Focus sentinel placed immediately before the main content region.
 * When the user Shift+Tabs from the page, focus lands here and is
 * redirected to the currently active sidebar link so reverse navigation
 * returns to the menu item they activated instead of cycling through
 * the bottom of the sidebar.
 */
const FocusSentinel = () => {
	const sentinelRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		const handleFocus = () => {
			const activeSidebarLink = document.querySelector(
				'nav[aria-label="Sidebar"] a[aria-current="page"]'
			) as HTMLElement | null
			activeSidebarLink?.focus()
		}

		const sentinel = sentinelRef.current
		sentinel?.addEventListener('focus', handleFocus)
		return () => sentinel?.removeEventListener('focus', handleFocus)
	}, [])

	return (
		<div
			ref={sentinelRef}
			tabIndex={0}
			className='sr-only outline-none'
			aria-label='Return to sidebar navigation'
			data-testid='focus-sentinel'
		/>
	)
}

const Router = () => {

	// Root element does not exist in testing environment :-(
	if (process.env.NODE_ENV !== 'test') {
		ReactModal.setAppElement('#root')
	}

	const mainRef = useRef<HTMLDivElement>(null)
	const { checkAccess, userData } = useContext(userContext);

	const location = useLocation();
	const validroutes = orderedRoutes.filter(route => {

		const hasAccess = !route.accessRequired || route.accessRequired.every(key => checkAccess(key));
		const hasRole = !route.rolesAllowed || route.rolesAllowed.some(role => role === userData?.role);
		if(route.name === 'Landing Page') {
			console.log({hasRole, hasAccess})
		}
		return hasRole && hasAccess;
	})
	const matches = useMemo(() => {
		const routes = validroutes.map(route => {
			return {
				...route,
				path: route.path,
				element: <ErrorBoundary FallbackComponent={Fallback} onError={logError}>{ route.component() }</ErrorBoundary>,
			}
		})
		return matchRoutes(routes, location);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location])

	const routeGroupDefaultPath: Record<string, string> = {
		admin: routes.adminDashboard.path,
		company: routes.companyDashboard.path,
	}

	const roleBasedRedirectPath = userData
		? routeGroupDefaultPath[userData.role] ?? routes.publicLogin.path
		: routes.publicLogin.path;

	const route = matches && matches.length > 0 ? matches[0].route : null
	const rendered = route ? renderMatches(matches) : null
	console.log('Shown route', route);

	useEffect(() => {
		if (route) {
			// preventScroll: on iOS Safari, focusing the pane otherwise scrolls the window and hides the top bar.
			mainRef.current?.focus({ preventScroll: true })
			// The pane is the same element across routes, so its scroll offset would otherwise carry over.
			mainRef.current?.scrollTo?.(0, 0)
		}
	}, [location.pathname, route])

	const darkSurface = route?.darkSurface === 'always' || (route?.darkSurface === 'loggedOut' && !userData)

	if (!route) {
		console.log(`Valid route not found for ${location.pathname}  navigating to `, roleBasedRedirectPath)
		return <Navigate to={roleBasedRedirectPath}/>
	}

	return (
		<div
			className={`flex flex-col h-dvh ${darkSurface ? 'bg-charcoal-700 surface-dark' : 'bg-charcoal-500'}`}
			data-testid='app-shell'
		>
			<TopBar />
			<div className="flex content-stretch grow overflow-hidden">
				{ route.sidebar && <Sidebar validRoutes={validroutes} /> }
				{ route.sidebar && <FocusSentinel /> }
				{/* tabIndex={-1} keeps the browser from adding this scrollable pane to the tab order with an unstyled default outline */}
				<div
					ref={mainRef}
					className="grow relative overflow-y-auto outline-none pb-[env(safe-area-inset-bottom)]"
					tabIndex={-1}
					data-testid='main-content'
				>
					{ !route.mobileReady && <SmallScreenNotice /> }
					{ rendered }
					<Notifications />
				</div>
			</div>
		</div>
	)
}

function Fallback({error, resetErrorBoundary}:{error: Error, resetErrorBoundary: Function}) {
	const location = useLocation();
	const [startLocation] = useState(location)

	useEffect(() => {
		if(location.pathname !== startLocation.pathname) {
			resetErrorBoundary()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location])
	return <span className="text-charcoal-300 p-8">Error: {error.message}</span>
}

function logError(error: Error) {
	console.error(error);
}

export default Router
